import {
  Component, EventEmitter, inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  KeyVarElement,
  Program,
  Programs,
  Site,
  SiteTariffs,
  Utility
} from '../../../domain/cloud-topology';
import { Observable, Subject } from 'rxjs';
import { SiteService } from '../../../services/site/site.service';
import {faCircle, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import { COINCIDENT_PEAK, DEMAND_RESPONSE } from "../../../domain/constants";
import { ConfirmModalComponent } from "../../shared/confirm-modal/confirm-modal.component";
import {takeUntil} from "rxjs/operators";
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-site-financial',
    templateUrl: './site-financial.component.html',
    styleUrls: ['./site-financial.component.scss'],
    standalone: false
})
export class SiteFinancialComponent implements OnChanges, OnDestroy {
  @Input() site: Site;
  @Input() financialDirty: boolean;
  @Output() financialDirtyChange = new EventEmitter<boolean>();
  isTariffPanelEnabled: boolean;
  isTariffFormDirty: boolean;
  isChargesFormDirty: boolean;
  siteUtilityTitle: boolean;
  siteTariffsTitle: boolean;
  siteChargesTitle: boolean;
  siteGridTitle: boolean;
  sitePeakTitle: boolean;
  activeIds: string[];
  chargesStr: string = '';
  siteTariffs: SiteTariffs;
  charges: KeyVarElement[];
  coincidentPeakPrograms: Program[];
  demandResponsePrograms: Program[];
  tariffNames: SiteTariffs;
  utility: Utility;
  circle = faCircle;
  info = faInfoCircle;
  active = 1;
  private subject$: Subject<void>;
  readonly dialog = inject(MatDialog);

  constructor(private siteService: SiteService) {
    this.subject$ = new Subject<void>();
  }

  ngOnChanges(changes: SimpleChanges) {
    // @ts-ignore
    this.site = changes['site']?.currentValue;
    if (this.site) {
      this.isTariffPanelEnabled = false;
      this.isTariffFormDirty = false;
      this.isChargesFormDirty = false;
      this.financialDirty = false;
      this.coincidentPeakPrograms = [];
      this.demandResponsePrograms = [];
      this.activeIds = []; //Make tabs close so template can reload.
      this.siteUtilityTitle = false;
      this.siteTariffsTitle = false;
      this.siteChargesTitle = false;
      this.siteGridTitle = false;
      this.sitePeakTitle = false;
      this.setSiteData();
    }

    if (this.site && changes['financialDirty'] == undefined) { // don't repull data if we are just detecting dirty flag.
      this.utility = null;
      this.charges = null;
      this.siteTariffs = null;
      this.setSiteData();
    }
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  setSiteData(): void {
    this.siteService.getUtility(this.site.id).pipe(takeUntil(this.subject$)).subscribe(siteUtility => {
      this.utility = siteUtility;
    });

    this.siteService.getSiteCharges(this.site.id).pipe(takeUntil(this.subject$)).subscribe(charges => {
      this.charges = charges;
      this.getOtherChargesTitle();
    });

    this.siteService.getSiteTariffs(this.site.id).pipe(takeUntil(this.subject$)).subscribe(siteTariffs => {
      this.siteTariffs = siteTariffs;
    });

    this.siteService.getTariffNames(this.site.id).pipe(takeUntil(this.subject$)).subscribe(tariffNames => {
      this.tariffNames = tariffNames;
    });

    this.siteService.getSiteTariffs(this.site.id).pipe(takeUntil(this.subject$)).subscribe(siteTariffs => {
      this.siteTariffs = siteTariffs;
      this.setTariffNames();
    });

    this.siteService.getPrograms(this.site.id, DEMAND_RESPONSE).pipe(takeUntil(this.subject$)).subscribe(programs => {
      this.demandResponsePrograms = programs;
    });

    this.siteService.getPrograms(this.site.id, COINCIDENT_PEAK).pipe(takeUntil(this.subject$)).subscribe(programs => {
      this.coincidentPeakPrograms = programs;
    });
  }

  toggleTariffPanel($event): void {
    this.isTariffPanelEnabled = $event;
    if(!$event) {
      this.getTariffsTitle();
    } else {
      this.financialDirtyChange.emit(true);
    }
  }

  setDirtyTariffForm($event): void {
    this.isTariffFormDirty = $event;
    if(!this.isTariffFormDirty) {
      this.setTariffNames();
      this.financialDirtyChange.emit(false);
    } else {
      this.financialDirtyChange.emit(true);
    }
  }

  setDirtyChargesForm($event): void {
    this.isChargesFormDirty = $event;
    this.getOtherChargesTitle();
    if (this.isChargesFormDirty) {
      this.financialDirtyChange.emit(true);
    }
  }

  private setTariffNames(): void {
    this.siteService.getTariffNames(this.site.id).pipe(takeUntil(this.subject$)).subscribe(tariffNames => {
      this.tariffNames = tariffNames;
    });
  }
  getTariffsTitle(): string {
    if (this.siteTariffs) {
      let tariffsStrArray = [];

      if (this.tariffNames.supplyAfter) {
        tariffsStrArray.push(`Supply After: ${this.tariffNames.supplyAfter}`);
      }

      if (this.tariffNames.deliveryAfter) {
        tariffsStrArray.push(`Delivery After: ${this.tariffNames.deliveryAfter}`);
      }

      return tariffsStrArray.join(', ');
    }

    return '';
  }

  getProgramPanelTitle(programs: Programs[]): string {
    let strArray = [];
    for (const program of programs) {
      strArray.push(program['name']);
    }

    if (strArray.length > 0) {
      return strArray.join(', ');
    } else {
      return 'None';
    }
  }

  getOtherChargesTitle(): void {
    let strArray = [];
    Object.entries(this.charges).forEach(([, element]) => {
      // @ts-ignore
      if( element && element['value'] > 0) {
        if(element['key'] == 'contractDemand') {
          strArray.push('Contract Demand'+': '+element['value']);
        }

        if(element['key'] == 'solarPPA') {
          strArray.push('Solar PPA'+': '+ element['value']);
        }
      }
    });

    this.chargesStr = strArray.join(', ');
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if(this.isChargesFormDirty || this.isTariffFormDirty || this.isTariffPanelEnabled) {
      const modalRef = this.dialog.open(ConfirmModalComponent, {
        data: { title: 'Warning', body: 'You have unsaved changes. Are you sure you wish to leave?' }
      });
       return modalRef.afterClosed();
    } else {
      return true;
    }
  }
}
